import React, { useState, useEffect } from "react";
import { Box, Skeleton, Typography, Button, Container } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

// Enhanced banner animations and effects
const bannerStyles = `
  /* Image Gallery Enhancements */
  .image-gallery-slide img {
    transition: all 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
    filter: brightness(1.05) contrast(1.03) saturate(1.05);
  }
  .image-gallery-slide:hover img {
    transform: scale(1.08) !important;
    filter: brightness(1.1) contrast(1.05) saturate(1.08);
  }
  .image-gallery .image-gallery-slide {
    overflow: hidden;
  }
  
  /* Navigation Arrows Enhancement */
  .image-gallery-nav .image-gallery-svg {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .image-gallery-nav:hover .image-gallery-svg {
    transform: scale(1.2);
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
  }
  
  /* Content Animations */
  .banner-content-fade {
    animation: contentFadeInUp 1.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  /* Pulse effect for CTAs */
  .banner-cta-pulse {
    animation: ctaPulse 2s ease-in-out infinite;
  }
  
  @keyframes contentFadeInUp {
    0% {
      opacity: 0;
      transform: translateY(40px) scale(0.95);
    }
    60% {
      opacity: 0.8;
      transform: translateY(10px) scale(0.98);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes ctaPulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
    }
  }
  
  /* Smooth transitions for all banner elements */
  .banner-element {
    transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
`;

// Inject styles
if (
  typeof document !== "undefined" &&
  !document.querySelector("#banner-animations")
) {
  const style = document.createElement("style");
  style.id = "banner-animations";
  style.textContent = bannerStyles;
  document.head.appendChild(style);
}

export default function FullWidthBanner({ banners, handleCTANavigation }) {
  const [imagesLoaded, setImagesLoaded] = useState({});

  // Handle image loading state
  const handleImageLoad = (imageUrl) => {
    setImagesLoaded((prev) => ({
      ...prev,
      [imageUrl]: true,
    }));
  };

  // Handle scroll down function
  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth",
    });
  };

  // Sort banners by Banner_Position for proper slider order
  const sortedBanners = banners.sort(
    (a, b) => (a.Banner_Position || 0) - (b.Banner_Position || 0)
  );

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", width: "100%", mb: 6 }}>
      <ImageGallery
        items={sortedBanners.map((banner) => ({
          original: `${process.env.BASEURL}${banner.Banner_Image_File_Path}`,
          banner: banner, // Store banner data for overlay access
        }))}
        showThumbnails={false}
        showFullscreenButton={false}
        showPlayButton={false}
        showBullets={false}
        autoPlay={true}
        slideInterval={12000}
        slideDuration={2000}
        infinite={true}
        renderItem={(item) => (
          <Box
            sx={{
              width: "100%",
              height: {
                xs: "450px",
                sm: "600px",
                md: "100vh",
                lg: "100vh",
                xl: "100vh",
              },
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(45deg, rgba(0,0,0,0.1), rgba(0,0,0,0.05))",
                zIndex: 1,
                opacity: 0,
                transition: "opacity 0.6s ease-in-out",
              },
              "&:hover::before": {
                opacity: 1,
              },
            }}
          >
            <img
              src={item.original}
              alt={item.banner?.Image_Heading_Text || "Banner"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
                transition: "all 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                transform: "scale(1)",
              }}
              className="banner-image"
              onLoad={() => handleImageLoad(item.original)}
            />
            {/* Loading overlay */}
            {!imagesLoaded[item.original] && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(30, 30, 30, 0.9)"
                      : "rgba(245, 245, 245, 0.9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  zIndex: 10,
                  backdropFilter: "blur(4px)",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)",
                  }}
                />
              </Box>
            )}
            {/* Overlay gradient and text section */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "flex-end",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.8) 100%)"
                      : "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.7) 100%)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                }}
              >
                <Container
                  maxWidth="lg"
                  sx={{
                    pointerEvents: "auto",
                    px: { xs: 3, md: 6 },
                    animation:
                      "slideUpFadeIn 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                    "@keyframes slideUpFadeIn": {
                      "0%": {
                        opacity: 0,
                        transform: "translateY(40px)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                    pb: { xs: 6, md: 8 },
                    position: "relative",
                  }}
                >
                  {/* Banner text content */}
                  <Box
                    sx={{
                      maxWidth: { xs: "100%", md: "35%" },
                      textAlign: "right",
                      ml: "auto",
                    }}
                  >
                    {item.banner?.Image_Heading_Text && (
                      <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                          color: "white",
                          fontWeight: 700,
                          fontSize: {
                            xs: "1.5rem",
                            sm: "1.8rem",
                            md: "2.2rem",
                            lg: "2.5rem",
                          },
                          lineHeight: 1.1,
                          mb: 2,
                          textShadow: "0 4px 12px rgba(0,0,0,0.7)",
                        }}
                      >
                        {item.banner.Image_Heading_Text}
                      </Typography>
                    )}

                    {item.banner?.Image_Sub_Heading_Text && (
                      <Typography
                        variant="h5"
                        sx={{
                          color: "rgba(255, 255, 255, 0.9)",
                          fontWeight: 400,
                          fontSize: {
                            xs: "0.9rem",
                            sm: "1rem",
                            md: "1.1rem",
                          },
                          lineHeight: 1.4,
                          mb: 2,
                          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                        }}
                      >
                        {item.banner.Image_Sub_Heading_Text}
                      </Typography>
                    )}

                    {item.banner?.Image_Text && (
                      <Typography
                        variant="body1"
                        sx={{
                          color: "rgba(255, 255, 255, 0.85)",
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.9rem",
                            md: "1rem",
                          },
                          lineHeight: 1.5,
                          mb: 3,
                          textShadow: "0 2px 6px rgba(0,0,0,0.5)",
                        }}
                      >
                        {item.banner.Image_Text}
                      </Typography>
                    )}

                    {/* CTA Button */}
                    {item.banner?.Banner_CTA_Button_Link &&
                      item.banner?.Banner_CTA_Text && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleCTANavigation(
                              item.banner.Banner_CTA_Button_Link
                            )
                          }
                          sx={{
                            px: 2.5,
                            py: 1,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            borderRadius: "50px",
                            textTransform: "none",
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            color: (theme) => theme.palette.primary.main,
                            backdropFilter: "blur(10px)",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                            "&:hover": {
                              backgroundColor: "white",
                              transform: "translateY(-2px)",
                              boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
                            },
                            transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                          }}
                        >
                          {item.banner.Banner_CTA_Text}
                        </Button>
                      )}
                  </Box>
                </Container>
              </Box>
            </Box>
          </Box>
        )}
      />

      {/* Animated scroll mouse indicator */}
      <Box
        onClick={handleScrollDown}
        sx={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 15,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          opacity: 0.9,
          transition: "all 0.3s ease",
          "&:hover": {
            opacity: 1,
            transform: "translateX(-50%) translateY(-5px)",
          },
        }}
      >
        {/* Mouse icon */}
        <Box
          sx={{
            width: 24,
            height: 36,
            border: "2px solid white",
            borderRadius: "15px",
            position: "relative",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}
        >
          {/* Animated scroll dot */}
          <Box
            sx={{
              width: 4,
              height: 8,
              backgroundColor: "white",
              borderRadius: "2px",
              position: "absolute",
              left: "50%",
              top: 6,
              transform: "translateX(-50%)",
              animation: "scrollBounce 2s infinite",
              "@keyframes scrollBounce": {
                "0%": {
                  opacity: 1,
                  transform: "translateX(-50%) translateY(0)",
                },
                "50%": {
                  opacity: 0.3,
                  transform: "translateX(-50%) translateY(12px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateX(-50%) translateY(0)",
                },
              },
            }}
          />
        </Box>

        {/* Animated arrow */}
        <KeyboardArrowDown
          sx={{
            color: "white",
            fontSize: 28,
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
            animation: "arrowBounce 2s infinite",
            "@keyframes arrowBounce": {
              "0%, 100%": {
                transform: "translateY(0)",
                opacity: 1,
              },
              "50%": {
                transform: "translateY(8px)",
                opacity: 0.7,
              },
            },
          }}
        />

        {/* Scroll text */}
        <Typography
          variant="caption"
          sx={{
            color: "white",
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "1px",
            textShadow: "0 2px 4px rgba(0,0,0,0.5)",
            opacity: 0.8,
          }}
        >
          Scroll
        </Typography>
      </Box>
    </Box>
  );
}
